import { HUB_META, resolveServiceDisplayName, type ServiceHubType } from '../../constants/serviceHubs';
import type { PartnerPayoutModel } from '../../constants/transportPartnerFlow';
import {
  ZONE_COMMISSION_RATE_DECIMALS,
  ZONE_COMMISSION_RATE_MAX,
  ZONE_COMMISSION_RATE_MIN,
  ZONE_DISTANCE_DECIMALS,
  ZONE_DISTANCE_MAX,
  ZONE_DISTANCE_MIN,
  ZONE_PRICE_DECIMALS,
  ZONE_PRICE_MAX,
  ZONE_PRICE_MIN,
  ZONE_SURGE_MULTIPLIER_DECIMALS,
  ZONE_SURGE_MULTIPLIER_MAX,
  ZONE_SURGE_MULTIPLIER_MIN,
  commissionRateUsesZonePercent,
} from '../../constants/transportPartnerFlow';

import type { ZonePricingInput } from '../../utils/zoneFareCalculator';

import { EMPTY_PRICING } from '../../utils/zoneFareCalculator';
import { roundToDecimalPlaces } from '../../utils/nonNegativeNumber';



export interface HubServiceOption {

  _id: string;

  name: string;

  slug: string;

  hubType: ServiceHubType;

  themeColor?: string;

}



export interface VehicleTypeOption {

  code: string;

  name: string;

}

export const FALLBACK_VEHICLES: VehicleTypeOption[] = [
  { code: 'bike', name: 'Bike' },
  { code: 'auto', name: 'Auto Rickshaw' },
  { code: 'sedan', name: 'Sedan' },
  { code: 'mini', name: 'Mini' },
  { code: 'suv', name: 'SUV' },
];

type PricingScheduleSlice = ZoneFormState['servicePricing'][0]['rideNow'];

export interface ZoneDetailPricingRow {
  serviceId: { _id: string; name?: string; slug?: string; hubType?: string } | string;
  scheduleType: 'ride_now' | 'ride_later' | 'default';
  vehicleTypeCode?: string;
  baseFare?: number;
  baseDistanceKm?: number;
  perKmRate?: number;
  perMinRate?: number;
  perKgRate?: number;
  deliveryCharge?: number;
  minimumFare?: number;
  freeWaitingMin?: number;
  waitingChargePerMin?: number;
}

export interface ZoneDetailForHydration {
  zone: {
    code: string;
    name: string;
    zoneLevel: 'PRIMARY' | 'SECONDARY';
    primaryZoneId?: string;
    country: string;
    currency: string;
    timezone: string;
    unit: 'KM' | 'MILE';
    color?: string;
    paymentTypes: string[];
    defaultPaymentType?: string;
    biddingEnabled: boolean;
    nonServiceZone: boolean;
    polygon: { lat: number; lng: number }[];
    serviceIds: Array<{ _id: string; name?: string; slug?: string; hubType?: string } | string>;
    metadata?: Record<string, unknown>;
  };
  servicePricing: ZoneDetailPricingRow[];
  surgePricing: Array<{
    serviceId?: { _id: string } | string;
    name: string;
    multiplier: number;
    startHour: number;
    endHour: number;
    daysOfWeek: number[];
    surgePerKm?: number;
    metadata?: { startTime?: string; endTime?: string; serviceCategoryCode?: string };
  }>;
}

function pricingRowToInput(p: ZoneDetailPricingRow): PricingScheduleSlice {
  return {
    baseFare: p.baseFare,
    baseDistanceKm: p.baseDistanceKm,
    perKmRate: p.perKmRate,
    perMinRate: p.perMinRate,
    perKgRate: p.perKgRate,
    deliveryCharge: p.deliveryCharge,
    minimumFare: p.minimumFare,
    freeWaitingMin: p.freeWaitingMin,
    waitingChargePerMin: p.waitingChargePerMin,
  };
}

function serviceIdFromRef(ref: ZoneDetailPricingRow['serviceId'] | undefined): string {
  if (!ref) return '';
  return typeof ref === 'string' ? ref : ref._id;
}

function isTransportServiceRef(
  sid: string,
  allServices: HubServiceOption[],
  pricingRows: ZoneDetailPricingRow[],
): boolean {
  const svc = allServices.find((s) => s._id === sid);
  if (svc) return isRideHailingService(svc.slug);
  const row = pricingRows.find((p) => serviceIdFromRef(p.serviceId) === sid);
  const populated = row?.serviceId;
  if (populated && typeof populated === 'object' && populated.slug) {
    return isRideHailingService(populated.slug);
  }
  return false;
}

/** Legacy zones stored transport fares at service level — promote to per-vehicle rows for the wizard. */
function migrateLegacyTransportPricing(
  _pricingMap: Map<string, { rideNow: PricingScheduleSlice; rideLater: PricingScheduleSlice }>,
  _vehicleMap: Map<string, VehicleSchedulePricing>,
  _serviceIds: string[],
  _allServices: HubServiceOption[],
  _pricingRows: ZoneDetailPricingRow[],
  _vehicleTypes: VehicleTypeOption[],
) {
  // Legacy service-level taxi pricing stays on servicePricing rows only.
}

function parseSelectedVehicleTypeCodesFromMetadata(metadata?: Record<string, unknown>): string[] {
  const raw = metadata?.selectedVehicleTypeCodes;
  if (!Array.isArray(raw)) return [];
  return raw.map((code) => String(code).trim()).filter(Boolean);
}

export function hydrateZoneFormFromDetail(
  detail: ZoneDetailForHydration,
  allServices: HubServiceOption[],
  vehicleTypes: VehicleTypeOption[] = FALLBACK_VEHICLES,
): ZoneFormState {
  const z = detail.zone;
  const pricingMap = new Map<string, { rideNow: PricingScheduleSlice; rideLater: PricingScheduleSlice }>();
  const vehicleMap = new Map<string, VehicleSchedulePricing>();

  for (const p of detail.servicePricing) {
    const sid = serviceIdFromRef(p.serviceId);
    const vehicleCode = p.vehicleTypeCode && p.vehicleTypeCode !== '__default__'
      ? p.vehicleTypeCode
      : undefined;
    const row = pricingRowToInput(p);

    if (vehicleCode) {
      if (!vehicleMap.has(vehicleCode)) {
        vehicleMap.set(vehicleCode, {
          vehicleTypeCode: vehicleCode,
          vehicleTypeName: vehicleTypes.find((v) => v.code === vehicleCode)?.name
            || vehicleCode.charAt(0).toUpperCase() + vehicleCode.slice(1),
          linkedServiceId: sid,
          rideNow: { ...EMPTY_PRICING },
          rideLater: { ...EMPTY_PRICING },
        });
      }
      const v = vehicleMap.get(vehicleCode)!;
      if (p.scheduleType === 'ride_later') v.rideLater = { ...v.rideLater, ...row };
      else v.rideNow = { ...v.rideNow, ...row };
      continue;
    }

    if (!pricingMap.has(sid)) pricingMap.set(sid, { rideNow: { ...EMPTY_PRICING }, rideLater: { ...EMPTY_PRICING } });
    const entry = pricingMap.get(sid)!;
    if (p.scheduleType === 'ride_later') entry.rideLater = { ...entry.rideLater, ...row };
    else if (p.scheduleType === 'default' || p.scheduleType === 'ride_now') entry.rideNow = { ...entry.rideNow, ...row };
  }

  const rawServiceIds = z.serviceIds.map((s) => (typeof s === 'string' ? s : s._id));
  const serviceIds = normalizeSelectedServiceIds(rawServiceIds, allServices);

  migrateLegacyTransportPricing(pricingMap, vehicleMap, serviceIds, allServices, detail.servicePricing, vehicleTypes);

  const servicePricing = serviceIds.map((sid) => {
    const svc = allServices.find((s) => s._id === sid);
    const populated = z.serviceIds.find((s) => (typeof s === 'string' ? s : s._id) === sid);
    const populatedObj = populated && typeof populated === 'object' ? populated : undefined;
    const base = svc
      ? buildServicePricingRow(svc)
      : {
        serviceId: sid,
        serviceName: populatedObj?.name || sid,
        slug: populatedObj?.slug || '',
        hubType: (populatedObj?.hubType || 'transport') as ServiceHubType,
        rideNow: { ...EMPTY_PRICING },
        rideLater: { ...EMPTY_PRICING },
      };
    const saved = pricingMap.get(sid);
    if (saved) {
      base.rideNow = { ...base.rideNow, ...saved.rideNow };
      base.rideLater = { ...base.rideLater, ...saved.rideLater };
    }
    return base;
  });

  const payout = parseZonePartnerPayoutFromMetadata(z.metadata);
  const savedVehicleCodes = parseSelectedVehicleTypeCodesFromMetadata(z.metadata);
  const vehiclePricingAll = Array.from(vehicleMap.values());
  const knownVehicleCodes = new Set(vehicleTypes.map((v) => v.code));
  const inferredVehicleCodes = Array.from(vehicleMap.keys()).filter((code) => knownVehicleCodes.has(code));
  const selectedVehicleTypeCodes = normalizeSelectedVehicleTypeCodes(
    savedVehicleCodes.length ? savedVehicleCodes : inferredVehicleCodes,
    vehicleTypes,
  );
  const selectedVehicleSet = new Set(selectedVehicleTypeCodes);
  const vehiclePricing = vehiclePricingAll.filter((v) => selectedVehicleSet.has(v.vehicleTypeCode));

  return {
    code: z.code,
    name: z.name,
    zoneLevel: z.zoneLevel,
    primaryZoneId: z.primaryZoneId || '',
    country: z.country,
    currency: z.currency,
    timezone: z.timezone,
    unit: z.unit,
    color: z.color || '#2563eb',
    paymentTypes: z.paymentTypes,
    defaultPaymentType: z.defaultPaymentType || z.paymentTypes[0] || 'cash',
    biddingEnabled: z.biddingEnabled,
    allowDriverCounterOffer: true,
    autoAcceptLowestBid: false,
    nonServiceZone: z.nonServiceZone,
    commissionRate: payout.commissionRate,
    partnerPayoutModel: payout.partnerPayoutModel,
    polygon: z.polygon || [],
    landPolygons: Array.isArray((z.metadata as { landPolygons?: { lat: number; lng: number }[][] } | undefined)?.landPolygons)
      ? ((z.metadata as { landPolygons: { lat: number; lng: number }[][] }).landPolygons)
      : [],
    selectedServiceIds: serviceIds,
    selectedVehicleTypeCodes,
    servicePricing,
    vehiclePricing,
    surgePricing: (detail.surgePricing ?? []).map((s) => ({
      serviceId: typeof s.serviceId === 'object' ? s.serviceId?._id : s.serviceId,
      serviceCategoryCode: s.metadata?.serviceCategoryCode,
      name: s.name,
      multiplier: roundToDecimalPlaces(s.multiplier, ZONE_SURGE_MULTIPLIER_DECIMALS),
      surgePerKm: roundToDecimalPlaces(s.surgePerKm ?? 0, ZONE_PRICE_DECIMALS),
      startTime: s.metadata?.startTime
        ?? `${String(Math.floor(s.startHour)).padStart(2, '0')}:${String(Math.round((s.startHour % 1) * 60)).padStart(2, '0')}`,
      endTime: s.metadata?.endTime
        ?? `${String(Math.floor(s.endHour)).padStart(2, '0')}:${String(Math.round((s.endHour % 1) * 60)).padStart(2, '0')}`,
      daysOfWeek: s.daysOfWeek,
    })),
    deliveryPartnerPricing: parseDeliveryPartnerPricingFromMetadata(z.metadata),
    deliveryIncentives: parseDeliveryIncentivesFromMetadata(z.metadata),
    porterPartnerPricing: parsePorterPartnerPricingFromMetadata(z.metadata),
    porterIncentives: parsePorterIncentivesFromMetadata(z.metadata),
    porterMasters: parsePorterMastersFromMetadata(z.metadata),
  };
}

export type ZoneStep0FieldErrors = Partial<Record<
  'country' | 'name' | 'code' | 'primaryZoneId' | 'paymentTypes' | 'defaultPaymentType' | 'services' | 'vehicles' | 'porter' | 'polygon' | 'commissionRate',
  string
>>;

/** Letters and spaces only — no digits or special characters. */
const ZONE_NAME_PATTERN = /^[\p{L}]+(?:\s+[\p{L}]+)*$/u;
const ZONE_NAME_INVALID_CHARS = /[^\p{L}\s]/gu;

export function sanitizeZoneName(raw: string): string {
  return raw.replace(ZONE_NAME_INVALID_CHARS, '').replace(/\s+/g, ' ');
}

export function validateZoneName(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return 'Zone name is required.';
  if (trimmed.length < 2) return 'Zone name must be at least 2 characters.';
  if (ZONE_NAME_INVALID_CHARS.test(trimmed) || !ZONE_NAME_PATTERN.test(trimmed)) {
    return 'Zone name can only contain letters (no numbers or special characters).';
  }
  return undefined;
}

export function isDuplicateZoneName(
  name: string,
  existing: Array<{ _id?: string; name: string }>,
  excludeId?: string,
): boolean {
  const normalized = name.trim().toLowerCase();
  if (!normalized) return false;
  return existing.some((z) => {
    if (excludeId && z._id === excludeId) return false;
    return z.name.trim().toLowerCase() === normalized;
  });
}

export function validateZoneCode(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return 'Zone code is required.';
  if (!/^[A-Z0-9_-]{2,32}$/i.test(trimmed)) {
    return 'Use 2–32 letters, numbers, hyphen or underscore.';
  }
  return undefined;
}

export function isDuplicateZoneCode(
  code: string,
  existing: Array<{ _id?: string; code: string }>,
  excludeId?: string,
): boolean {
  const normalized = code.trim().toUpperCase();
  if (!normalized) return false;
  return existing.some((z) => {
    if (excludeId && z._id === excludeId) return false;
    return z.code.trim().toUpperCase() === normalized;
  });
}

export function validateZoneCommissionRate(
  value: number | string | null | undefined,
  payoutModel: PartnerPayoutModel,
): string | undefined {
  if (!commissionRateUsesZonePercent(payoutModel)) return undefined;

  if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return 'Commission rate is required.';
  }

  const n = Number(value);
  if (n < ZONE_COMMISSION_RATE_MIN) return 'Commission rate cannot be negative.';
  if (n > ZONE_COMMISSION_RATE_MAX) return `Commission rate cannot exceed ${ZONE_COMMISSION_RATE_MAX}%.`;

  const factor = 10 ** ZONE_COMMISSION_RATE_DECIMALS;
  if (Math.abs(n * factor - Math.round(n * factor)) > 1e-6) {
    return `Use at most ${ZONE_COMMISSION_RATE_DECIMALS} decimal places.`;
  }

  return undefined;
}

export function validateZoneDistanceKm(
  value: number | string | null | undefined,
): string | undefined {
  if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return 'Enter a valid distance.';
  }

  const n = Number(value);
  if (n < ZONE_DISTANCE_MIN) return 'Distance cannot be negative.';
  if (n > ZONE_DISTANCE_MAX) {
    return `Distance cannot exceed ${ZONE_DISTANCE_MAX.toLocaleString('en-IN')}.`;
  }

  const factor = 10 ** ZONE_DISTANCE_DECIMALS;
  if (Math.abs(n * factor - Math.round(n * factor)) > 1e-6) {
    return `Use at most ${ZONE_DISTANCE_DECIMALS} decimal places.`;
  }

  return undefined;
}

export function validateZonePricingAmount(
  value: number | string | null | undefined,
): string | undefined {
  if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return 'Enter a valid amount.';
  }

  const n = Number(value);
  if (n < ZONE_PRICE_MIN) return 'Amount cannot be negative.';
  if (n > ZONE_PRICE_MAX) {
    return `Amount cannot exceed ${ZONE_PRICE_MAX.toLocaleString('en-IN')}.`;
  }

  const factor = 10 ** ZONE_PRICE_DECIMALS;
  if (Math.abs(n * factor - Math.round(n * factor)) > 1e-6) {
    return `Use at most ${ZONE_PRICE_DECIMALS} decimal places.`;
  }

  return undefined;
}

export function validateDeliveryPartnerPricingForm(
  pricing: DeliveryPartnerPricingForm,
): string | undefined {
  const amountChecks: Array<[string, number]> = [
    ['Base amount', pricing.baseAmount],
    ['Per-km charge', pricing.remainingKmRate],
    ['Pickup charge', pricing.pickupCharge],
    ['Drop charge', pricing.dropCharge],
    ['Waiting charge per minute', pricing.waitingChargePerMinute],
    ['Minimum guarantee', pricing.minimumGuarantee],
  ];
  for (const [label, amount] of amountChecks) {
    const err = validateZonePricingAmount(amount);
    if (err) return `${label}: ${err}`;
  }

  const distanceChecks: Array<[string, number]> = [
    ['Base KM', pricing.baseKm],
    ['Maximum delivery distance', pricing.maximumDeliveryKm],
  ];
  for (const [label, distance] of distanceChecks) {
    const err = validateZoneDistanceKm(distance);
    if (err) return `${label}: ${err}`;
  }

  return undefined;
}

export function getZonePartnerPricingErrors(
  form: ZoneFormState,
  allServices: HubServiceOption[],
): string | undefined {
  if (formHasSelectedFoodGrocery(form, allServices)) {
    const pricingErr = validateDeliveryPartnerPricingForm(form.deliveryPartnerPricing);
    if (pricingErr) return `Food/Grocery partner pricing — ${pricingErr}`;

    for (const inc of form.deliveryIncentives) {
      const incErr = validateZonePricingAmount(inc.amount);
      if (incErr) return `Food/Grocery incentive — ${incErr}`;
    }
    const deliveryIncDup = validateIncentiveListUnique(form.deliveryIncentives);
    if (deliveryIncDup) return `Food/Grocery incentive — ${deliveryIncDup}`;

    for (const sp of activeServicePricing(form, isFoodOrGroceryDelivery, allServices)) {
      const fees = sp.rideNow;
      if (!fees) continue;
      const distErr = validateZoneDistanceKm(fees.baseDistanceKm ?? 0);
      if (distErr) return `${sp.serviceName || 'Delivery'} customer fee — base distance: ${distErr}`;
      for (const [label, amount] of [
        ['Delivery charge', fees.deliveryCharge],
        ['Per-km charge', fees.perKmRate],
      ] as const) {
        const err = validateZonePricingAmount(amount ?? 0);
        if (err) return `${sp.serviceName || 'Delivery'} customer fee — ${label}: ${err}`;
      }
    }
  }

  if (formHasSelectedPorter(form, allServices)) {
    const pricingErr = validateDeliveryPartnerPricingForm(form.porterPartnerPricing);
    if (pricingErr) return `Porter partner pricing — ${pricingErr}`;

    for (const inc of form.porterIncentives) {
      const incErr = validateZonePricingAmount(inc.amount);
      if (incErr) return `Porter incentive — ${incErr}`;
    }
    const porterIncDup = validateIncentiveListUnique(form.porterIncentives);
    if (porterIncDup) return `Porter incentive — ${porterIncDup}`;

    for (const sp of activeServicePricing(form, isPorterService, allServices)) {
      const fees = sp.rideNow;
      if (!fees) continue;
      for (const [label, amount] of [
        ['Base fare', fees.baseFare],
        ['Per-km charge', fees.perKmRate],
        ['Per min', fees.perMinRate],
        ['Per kg', fees.perKgRate],
      ] as const) {
        const err = validateZonePricingAmount(amount ?? 0);
        if (err) return `Porter customer fee — ${label}: ${err}`;
      }
    }
  }

  return undefined;
}

export function validateSurgeRuleName(name: string): string | undefined {
  if (!name.trim()) return 'Rule name is required.';
  return undefined;
}

/** Minutes since midnight from HH:mm (24h). */
export function parseSurgeTimeToMinutes(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((value || '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || h < 0 || h > 23 || !Number.isFinite(min) || min < 0 || min > 59) return null;
  return h * 60 + min;
}

/** Same-day surge window — end must be strictly after start. */
export function validateSurgeTimeWindow(startTime: string, endTime: string): string | undefined {
  const start = parseSurgeTimeToMinutes(startTime);
  const end = parseSurgeTimeToMinutes(endTime);
  if (start == null || end == null) return 'Enter valid start and end times.';
  if (end <= start) return 'End time must be later than start time on the same day.';
  return undefined;
}

export function getSurgeTimeErrors(form: ZoneFormState): Record<number, string> {
  const errors: Record<number, string> = {};
  form.surgePricing.forEach((row, idx) => {
    const err = validateSurgeTimeWindow(row.startTime, row.endTime);
    if (err) errors[idx] = err;
  });
  return errors;
}

export function validateSurgeMultiplier(
  value: number | string | null | undefined,
): string | undefined {
  if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return 'Enter a valid multiplier.';
  }
  const n = Number(value);
  if (n < ZONE_SURGE_MULTIPLIER_MIN) {
    return `Multiplier must be at least ${ZONE_SURGE_MULTIPLIER_MIN}.`;
  }
  if (n > ZONE_SURGE_MULTIPLIER_MAX) {
    return `Multiplier cannot exceed ${ZONE_SURGE_MULTIPLIER_MAX}.`;
  }
  const factor = 10 ** ZONE_SURGE_MULTIPLIER_DECIMALS;
  if (Math.abs(n * factor - Math.round(n * factor)) > 1e-6) {
    return `Use at most ${ZONE_SURGE_MULTIPLIER_DECIMALS} decimal places.`;
  }
  return undefined;
}

export function normalizeSurgeRuleName(name: string): string {
  return name.trim().toLowerCase();
}

export function validateSurgeRuleNameUnique(
  name: string,
  rows: SurgeRow[],
  excludeIndex?: number,
): string | undefined {
  const normalized = normalizeSurgeRuleName(name);
  if (!normalized) return undefined;
  const duplicate = rows.some(
    (row, idx) => idx !== excludeIndex && normalizeSurgeRuleName(row.name) === normalized,
  );
  if (!duplicate) return undefined;
  return 'This rule name is already used. Each surge rule must have a unique name.';
}

export function getSurgeRuleNameErrors(form: ZoneFormState): Record<number, string> {
  const errors: Record<number, string> = {};
  form.surgePricing.forEach((row, idx) => {
    const requiredErr = validateSurgeRuleName(row.name);
    if (requiredErr) {
      errors[idx] = requiredErr;
      return;
    }
    const dupErr = validateSurgeRuleNameUnique(row.name, form.surgePricing, idx);
    if (dupErr) errors[idx] = dupErr;
  });
  return errors;
}

export function getZoneSurgePricingErrors(form: ZoneFormState): string | undefined {
  for (let i = 0; i < form.surgePricing.length; i += 1) {
    const row = form.surgePricing[i];
    const requiredErr = validateSurgeRuleName(row.name);
    if (requiredErr) return `Surge rule ${i + 1}: ${requiredErr}`;
    const dupErr = validateSurgeRuleNameUnique(row.name, form.surgePricing, i);
    if (dupErr) return `Surge rule ${i + 1}: ${dupErr}`;
    const surgePerKmErr = validateZonePricingAmount(row.surgePerKm ?? 0);
    if (surgePerKmErr) return `Surge rule ${i + 1}: Surge per km — ${surgePerKmErr}`;
    const timeErr = validateSurgeTimeWindow(row.startTime, row.endTime);
    if (timeErr) return `Surge rule ${i + 1}: ${timeErr}`;
    const multiplierErr = validateSurgeMultiplier(row.multiplier);
    if (multiplierErr) return `Surge rule ${i + 1}: Multiplier — ${multiplierErr}`;
  }
  return undefined;
}

export function validateZoneWholeNumber(
  value: number | string | null | undefined,
): string | undefined {
  if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return 'Enter a valid number.';
  }

  const n = Number(value);
  if (n < 0) return 'Value cannot be negative.';
  if (!Number.isInteger(n)) return 'Must be a whole number.';

  return undefined;
}

/** Trip duration preview — must be a positive whole number (minutes). */
export function validateZoneDurationMinutes(
  value: number | string | null | undefined,
): string | undefined {
  const wholeError = validateZoneWholeNumber(value);
  if (wholeError) return wholeError;
  const n = Number(value);
  if (n <= 0) return 'Duration must be greater than 0.';
  return undefined;
}

export function getStep0FieldErrors(
  form: ZoneFormState,
  allServices: HubServiceOption[],
  options?: {
    existingZones?: Array<{ _id: string; name: string; code: string }>;
    excludeZoneId?: string;
  },
): ZoneStep0FieldErrors {
  const errors: ZoneStep0FieldErrors = {};
  if (!form.country?.trim()) errors.country = 'Country is required.';
  const nameError = validateZoneName(form.name);
  if (nameError) errors.name = nameError;
  else if (
    options?.existingZones
    && isDuplicateZoneName(form.name, options.existingZones, options.excludeZoneId)
  ) {
    errors.name = 'Zone name already exists.';
  }
  const codeError = validateZoneCode(form.code);
  if (codeError) errors.code = codeError;
  else if (
    options?.existingZones
    && isDuplicateZoneCode(form.code, options.existingZones, options.excludeZoneId)
  ) {
    errors.code = 'Zone code already exists.';
  }
  if (form.zoneLevel === 'SECONDARY' && !form.primaryZoneId) {
    errors.primaryZoneId = 'Primary zone is required for a secondary zone.';
  }
  if (!form.paymentTypes.length) errors.paymentTypes = 'Select at least one payment type.';
  if (form.paymentTypes.length && !form.paymentTypes.includes(form.defaultPaymentType)) {
    errors.defaultPaymentType = 'Choose a default from the selected payment types.';
  }
  if (!form.selectedServiceIds.length) errors.services = 'Select at least one service.';
  if (form.polygon.length < 3) {
    errors.polygon = 'Draw the zone on the map (at least 3 click points).';
  }
  const hasRideHailing = form.selectedServiceIds.some((id) => {
    const svc = allServices.find((s) => s._id === id);
    return svc && isRideHailingService(svc.slug);
  }) || form.vehiclePricing.length > 0;
  if (hasRideHailing && !form.selectedVehicleTypeCodes.length) {
    errors.vehicles = 'Select at least one transport vehicle type.';
  }
  const hasPorter = form.selectedServiceIds.some((id) => {
    const svc = allServices.find((s) => s._id === id);
    return svc && isPorterService(svc.slug);
  });
  if (hasPorter && !form.porterMasters.vehicleIds.length) {
    errors.porter = 'Select at least one Porter vehicle for this zone.';
  }
  const commissionError = validateZoneCommissionRate(form.commissionRate, form.partnerPayoutModel);
  if (commissionError) errors.commissionRate = commissionError;
  return errors;
}

export function getStep0ContinueBlockReason(
  form: ZoneFormState,
  allServices: HubServiceOption[],
  options?: {
    existingZones?: Array<{ _id: string; name: string; code: string }>;
    excludeZoneId?: string;
  },
): string | null {
  const errors = getStep0FieldErrors(form, allServices, options);
  return Object.values(errors)[0] || null;
}



export interface VehicleSchedulePricing {

  vehicleTypeCode: string;

  vehicleTypeName: string;

  linkedServiceId: string;

  rideNow: ZonePricingInput;

  rideLater: ZonePricingInput;

}



export interface ServiceSchedulePricing {

  serviceId: string;

  serviceName: string;

  slug: string;

  hubType: ServiceHubType;

  rideNow: ZonePricingInput;

  rideLater: ZonePricingInput;

}



export interface SurgeRow {

  serviceId?: string;

  /** On-demand catalog category (e.g. plumber) when serviceId is an on_demand hub service. */
  serviceCategoryCode?: string;

  name: string;

  multiplier: number;

  surgePerKm?: number;

  startTime: string;

  endTime: string;

  daysOfWeek: number[];

}



export interface DeliveryPartnerPricingForm {
  baseKm: number;
  baseAmount: number;
  remainingKmRate: number;
  pickupCharge: number;
  dropCharge: number;
  waitingChargePerMinute: number;
  minimumGuarantee: number;
  maximumDeliveryKm: number;
  isActive: boolean;
}

export interface DeliveryIncentiveForm {
  id: string;
  incentiveType: string;
  amount: number;
  isActive: boolean;
}

export const DELIVERY_PARTNER_SLUGS = new Set<string>();
export const PORTER_SERVICE_SLUG = 'porter-service';
export const ZONE_PARTNER_PRICING_SLUGS = new Set([...DELIVERY_PARTNER_SLUGS, PORTER_SERVICE_SLUG]);

export interface PorterZoneMastersForm {
  vehicleIds: string[];
  categoryIds: string[];
  packageIds: string[];
}

export function emptyPorterMasters(): PorterZoneMastersForm {
  return { vehicleIds: [], categoryIds: [], packageIds: [] };
}

export function isPorterService(slug: string) {
  return slug === PORTER_SERVICE_SLUG;
}

export const DELIVERY_INCENTIVE_OPTIONS = [
  { value: 'peak_hour', label: 'Peak Hour' },
  { value: 'rain', label: 'Rain' },
  { value: 'night', label: 'Night' },
  { value: 'heavy_order', label: 'Heavy Order' },
  { value: 'multi_order', label: 'Multi Order' },
  { value: 'festival', label: 'Festival' },
] as const;

export function normalizeIncentiveType(raw: string): string {
  const trimmed = (raw || '').trim();
  if (!trimmed) return DELIVERY_INCENTIVE_OPTIONS[0].value;
  const slug = trimmed.toLowerCase().replace(/[\s-]+/g, '_');
  const byValue = DELIVERY_INCENTIVE_OPTIONS.find((o) => o.value === slug || o.value === trimmed);
  if (byValue) return byValue.value;
  const byLabel = DELIVERY_INCENTIVE_OPTIONS.find(
    (o) => o.label.toLowerCase() === trimmed.toLowerCase(),
  );
  if (byLabel) return byLabel.value;
  if (slug === 'peak_hour_bonus') return 'peak_hour';
  return slug;
}

export function incentiveTypeLabel(type: string): string {
  const normalized = normalizeIncentiveType(type);
  return DELIVERY_INCENTIVE_OPTIONS.find((o) => o.value === normalized)?.label || type;
}

export function validateIncentiveTypeUnique(
  incentiveType: string,
  incentives: DeliveryIncentiveForm[],
  excludeId?: string,
): string | undefined {
  const normalized = normalizeIncentiveType(incentiveType);
  const duplicate = incentives.some(
    (i) => normalizeIncentiveType(i.incentiveType) === normalized && i.id !== excludeId,
  );
  if (!duplicate) return undefined;
  return `${incentiveTypeLabel(normalized)} is already added. Each incentive type can only be used once.`;
}

export function validateIncentiveListUnique(
  incentives: DeliveryIncentiveForm[],
): string | undefined {
  const seen = new Set<string>();
  for (const inc of incentives) {
    const key = normalizeIncentiveType(inc.incentiveType);
    if (seen.has(key)) {
      return `${incentiveTypeLabel(key)} is already added. Each incentive type can only be used once.`;
    }
    seen.add(key);
  }
  return undefined;
}

export function firstAvailableIncentiveType(incentives: DeliveryIncentiveForm[]): string {
  return DELIVERY_INCENTIVE_OPTIONS.find(
    (o) => !incentives.some((i) => normalizeIncentiveType(i.incentiveType) === o.value),
  )?.value ?? DELIVERY_INCENTIVE_OPTIONS[0].value;
}

export function allIncentiveTypesUsed(incentives: DeliveryIncentiveForm[]): boolean {
  return DELIVERY_INCENTIVE_OPTIONS.every(
    (o) => incentives.some((i) => normalizeIncentiveType(i.incentiveType) === o.value),
  );
}

/** Keep the first incentive row for each type (legacy / API duplicates). */
export function dedupeIncentivesByType(incentives: DeliveryIncentiveForm[]): DeliveryIncentiveForm[] {
  const seen = new Set<string>();
  return incentives
    .map((i) => ({ ...i, incentiveType: normalizeIncentiveType(i.incentiveType) }))
    .filter((i) => {
      if (seen.has(i.incentiveType)) return false;
      seen.add(i.incentiveType);
      return true;
    });
}

export function emptyDeliveryPartnerPricing(): DeliveryPartnerPricingForm {
  return {
    baseKm: 3,
    baseAmount: 35,
    remainingKmRate: 6,
    pickupCharge: 5,
    dropCharge: 5,
    waitingChargePerMinute: 0,
    minimumGuarantee: 50,
    maximumDeliveryKm: 15,
    isActive: true,
  };
}

export function emptyDeliveryIncentives(): DeliveryIncentiveForm[] {
  return [];
}

export function newDeliveryIncentive(partial?: Partial<DeliveryIncentiveForm>): DeliveryIncentiveForm {
  return {
    id: partial?.id || `inc_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    incentiveType: partial?.incentiveType || 'peak_hour',
    amount: partial?.amount ?? 0,
    isActive: partial?.isActive !== false,
  };
}

export function isFoodOrGroceryDelivery(slug: string) {
  return DELIVERY_PARTNER_SLUGS.has(slug);
}

export function usesZonePartnerPricing(slug: string) {
  return ZONE_PARTNER_PRICING_SLUGS.has(slug);
}

export function formHasDeliveryPartnerPricing(form: ZoneFormState, allServices: HubServiceOption[]) {
  return form.selectedServiceIds.some((id) => {
    const svc = allServices.find((s) => s._id === id);
    return svc && usesZonePartnerPricing(svc.slug);
  });
}

/** Ride Now / Ride Later steps — transport vehicles + services not priced in Partner step */
export function formNeedsSchedulePricing(form: ZoneFormState) {
  const hasTransport = activeVehiclePricing(form).length > 0;
  const selected = new Set(form.selectedServiceIds);
  const hasOtherServices = form.servicePricing.some(
    (sp) => selected.has(sp.serviceId) && !isRideHailingService(sp.slug) && !usesZonePartnerPricing(sp.slug),
  );
  return hasTransport || hasOtherServices;
}

/** Transport vehicle pricing rows that are still selected in step 1. */
export function activeVehiclePricing(form: ZoneFormState): VehicleSchedulePricing[] {
  const selected = new Set(form.selectedVehicleTypeCodes);
  return form.vehiclePricing.filter((v) => selected.has(v.vehicleTypeCode));
}

export function configuredTransportVehicleCount(
  form: ZoneFormState,
  allServices?: HubServiceOption[],
  vehicleTypes?: VehicleTypeOption[],
): number {
  if (!formHasSelectedRideHailing(form, allServices)) return 0;
  const codes = vehicleTypes?.length
    ? normalizeSelectedVehicleTypeCodes(form.selectedVehicleTypeCodes, vehicleTypes)
    : form.selectedVehicleTypeCodes;
  return codes.length;
}

/** Dedupe service ids and drop ids no longer in the tenant catalog. */
export function normalizeSelectedServiceIds(
  ids: string[],
  catalog: HubServiceOption[],
): string[] {
  const catalogIds = new Set(catalog.map((s) => s._id));
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of ids) {
    if (!catalogIds.has(id) || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }
  return result;
}

/** Dedupe vehicle codes and drop codes not present in the vehicle master. */
export function normalizeSelectedVehicleTypeCodes(
  codes: string[],
  vehicleTypes: VehicleTypeOption[],
): string[] {
  const known = new Set(vehicleTypes.map((v) => v.code));
  const seen = new Set<string>();
  const result: string[] = [];
  for (const code of codes) {
    if (!known.has(code) || seen.has(code)) continue;
    seen.add(code);
    result.push(code);
  }
  return result;
}

/** Services the user selected on step 1 (catalog + pool aware). */
export function configuredZoneServiceCount(
  form: ZoneFormState,
  allServices: HubServiceOption[],
  servicePool?: HubServiceOption[],
): number {
  const pool = servicePool ?? allServices;
  const poolIds = new Set(pool.map((s) => s._id));
  return normalizeSelectedServiceIds(form.selectedServiceIds, allServices)
    .filter((id) => poolIds.has(id))
    .length;
}

export function resolveZoneServicePool(
  form: ZoneFormState,
  allServices: HubServiceOption[],
  primaryZones: Array<{ _id: string; serviceIds?: Array<{ _id: string } | string> }>,
): HubServiceOption[] {
  if (form.zoneLevel === 'SECONDARY' && form.primaryZoneId) {
    const primary = primaryZones.find((z) => z._id === form.primaryZoneId);
    const ids = (primary?.serviceIds || []).map((s) => (typeof s === 'string' ? s : s._id));
    if (ids.length) return allServices.filter((s) => ids.includes(s._id));
  }
  return allServices;
}

/** Keep service + transport vehicle selections aligned with catalog/masters. */
export function reconcileZoneFormCatalog(
  form: ZoneFormState,
  allServices: HubServiceOption[],
  servicePool: HubServiceOption[],
  vehicleTypes: VehicleTypeOption[],
): ZoneFormState {
  let next = filterFormServicesToPool(form, servicePool, allServices);
  const selectedVehicleTypeCodes = normalizeSelectedVehicleTypeCodes(
    next.selectedVehicleTypeCodes,
    vehicleTypes,
  );
  const knownVehicleCodes = new Set(vehicleTypes.map((v) => v.code));
  const vehiclePricing = next.vehiclePricing
    .filter((v) => knownVehicleCodes.has(v.vehicleTypeCode))
    .map((v) => {
      const name = vehicleTypes.find((t) => t.code === v.vehicleTypeCode)?.name;
      return name && name !== v.vehicleTypeName ? { ...v, vehicleTypeName: name } : v;
    });
  const namesChanged = vehiclePricing.some((v) => {
    const prev = next.vehiclePricing.find((p) => p.vehicleTypeCode === v.vehicleTypeCode);
    return !prev || prev.vehicleTypeName !== v.vehicleTypeName;
  });
  const vehiclesChanged = selectedVehicleTypeCodes.length !== next.selectedVehicleTypeCodes.length
    || selectedVehicleTypeCodes.some((code, idx) => code !== next.selectedVehicleTypeCodes[idx])
    || vehiclePricing.length !== next.vehiclePricing.length
    || namesChanged;
  if (!vehiclesChanged) return next;
  return syncZoneFormWithSelectedServices({
    ...next,
    selectedVehicleTypeCodes,
    vehiclePricing,
  }, allServices);
}

export function serviceSlugFromCatalog(
  serviceId: string,
  allServices: HubServiceOption[],
  fallback = '',
): string {
  return allServices.find((s) => s._id === serviceId)?.slug ?? fallback;
}

export function formHasSelectedFoodGrocery(
  form: ZoneFormState,
  allServices?: HubServiceOption[],
) {
  if (allServices?.length) {
    return form.selectedServiceIds.some((id) => isFoodOrGroceryDelivery(serviceSlugFromCatalog(id, allServices)));
  }
  const selected = new Set(form.selectedServiceIds);
  return form.servicePricing.some(
    (sp) => selected.has(sp.serviceId) && isFoodOrGroceryDelivery(sp.slug),
  );
}

export function formHasSelectedPorter(
  form: ZoneFormState,
  allServices?: HubServiceOption[],
) {
  if (allServices?.length) {
    return form.selectedServiceIds.some((id) => isPorterService(serviceSlugFromCatalog(id, allServices)));
  }
  const selected = new Set(form.selectedServiceIds);
  return form.servicePricing.some(
    (sp) => selected.has(sp.serviceId) && isPorterService(sp.slug),
  );
}

export function formHasSelectedRideHailing(
  form: ZoneFormState,
  allServices?: HubServiceOption[],
) {
  if (allServices?.length) {
    return form.selectedServiceIds.some((id) => isRideHailingService(serviceSlugFromCatalog(id, allServices)));
  }
  const selected = new Set(form.selectedServiceIds);
  return form.servicePricing.some(
    (sp) => selected.has(sp.serviceId) && isRideHailingService(sp.slug),
  );
}

function deliveryPartnerPricingEqual(a: DeliveryPartnerPricingForm, b: DeliveryPartnerPricingForm) {
  return (
    a.baseKm === b.baseKm
    && a.baseAmount === b.baseAmount
    && a.remainingKmRate === b.remainingKmRate
    && a.pickupCharge === b.pickupCharge
    && a.dropCharge === b.dropCharge
    && a.waitingChargePerMinute === b.waitingChargePerMinute
    && a.minimumGuarantee === b.minimumGuarantee
    && a.maximumDeliveryKm === b.maximumDeliveryKm
    && a.isActive === b.isActive
  );
}

function porterMastersEqual(a: PorterZoneMastersForm, b: PorterZoneMastersForm) {
  const eq = (xs: string[], ys: string[]) => xs.length === ys.length && xs.every((v, i) => v === ys[i]);
  return eq(a.vehicleIds, b.vehicleIds) && eq(a.categoryIds, b.categoryIds) && eq(a.packageIds, b.packageIds);
}

/** Drop pricing/partner config for services no longer selected in step 1. */
export function syncZoneFormWithSelectedServices(
  form: ZoneFormState,
  allServices: HubServiceOption[],
): ZoneFormState {
  const selectedServiceIds = normalizeSelectedServiceIds(form.selectedServiceIds, allServices);
  const selected = new Set(selectedServiceIds);
  const servicePricing = form.servicePricing.filter((sp) => selected.has(sp.serviceId));

  const hasFoodGrocery = formHasSelectedFoodGrocery(form, allServices);
  const hasPorter = formHasSelectedPorter(form, allServices);
  const hasRideHailing = selectedServiceIds.some((id) => {
    const slug = serviceSlugFromCatalog(id, allServices);
    return isRideHailingService(slug);
  });

  const deliveryPartnerPricing = hasFoodGrocery
    ? form.deliveryPartnerPricing
    : emptyDeliveryPartnerPricing();
  const deliveryIncentives = hasFoodGrocery ? form.deliveryIncentives : emptyDeliveryIncentives();
  const porterPartnerPricing = hasPorter ? form.porterPartnerPricing : emptyDeliveryPartnerPricing();
  const porterIncentives = hasPorter ? form.porterIncentives : emptyDeliveryIncentives();
  const porterMasters = hasPorter ? form.porterMasters : emptyPorterMasters();
  const selectedVehicleTypeCodes = hasRideHailing ? form.selectedVehicleTypeCodes : [];
  const selectedVehicleCodes = new Set(selectedVehicleTypeCodes);
  const vehiclePricing = hasRideHailing
    ? form.vehiclePricing.filter((v) => selectedVehicleCodes.has(v.vehicleTypeCode))
    : [];

  const surgePricing = form.surgePricing.map((row) => (
    row.serviceId && !selected.has(row.serviceId)
      ? { ...row, serviceId: undefined, serviceCategoryCode: undefined }
      : row
  ));

  const idsChanged = selectedServiceIds.length !== form.selectedServiceIds.length
    || selectedServiceIds.some((id, idx) => id !== form.selectedServiceIds[idx]);
  const servicePricingChanged = servicePricing.length !== form.servicePricing.length
    || servicePricing.some((sp, idx) => sp.serviceId !== form.servicePricing[idx]?.serviceId);
  const surgeChanged = surgePricing.some((row, idx) => row.serviceId !== form.surgePricing[idx]?.serviceId);
  const partnerChanged = !deliveryPartnerPricingEqual(deliveryPartnerPricing, form.deliveryPartnerPricing)
    || deliveryIncentives !== form.deliveryIncentives
    || !deliveryPartnerPricingEqual(porterPartnerPricing, form.porterPartnerPricing)
    || porterIncentives !== form.porterIncentives
    || !porterMastersEqual(porterMasters, form.porterMasters);
  const transportChanged = selectedVehicleTypeCodes !== form.selectedVehicleTypeCodes
    || vehiclePricing !== form.vehiclePricing;

  if (!idsChanged && !servicePricingChanged && !surgeChanged && !partnerChanged && !transportChanged) {
    return form;
  }

  return {
    ...form,
    selectedServiceIds,
    servicePricing,
    surgePricing,
    deliveryPartnerPricing,
    deliveryIncentives,
    porterPartnerPricing,
    porterIncentives,
    porterMasters,
    selectedVehicleTypeCodes,
    vehiclePricing,
  };
}

/** Restrict selected services/pricing to an allowed pool (e.g. secondary zone primary). */
export function filterFormServicesToPool(
  form: ZoneFormState,
  servicePool: HubServiceOption[],
  allServices: HubServiceOption[],
): ZoneFormState {
  const poolIds = new Set(servicePool.map((s) => s._id));
  const nextSelected = normalizeSelectedServiceIds(form.selectedServiceIds, allServices)
    .filter((id) => poolIds.has(id));
  const selectionChanged = nextSelected.length !== form.selectedServiceIds.length
    || nextSelected.some((id, idx) => id !== form.selectedServiceIds[idx]);
  if (!selectionChanged) return syncZoneFormWithSelectedServices(form, allServices);

  return syncZoneFormWithSelectedServices({
    ...form,
    selectedServiceIds: nextSelected,
    servicePricing: form.servicePricing.filter((sp) => poolIds.has(sp.serviceId)),
  }, allServices);
}

export function activeServicePricing(
  form: ZoneFormState,
  predicate?: (slug: string) => boolean,
  allServices?: HubServiceOption[],
) {
  const selected = new Set(form.selectedServiceIds);
  return form.servicePricing.filter((sp) => {
    if (!selected.has(sp.serviceId)) return false;
    const slug = allServices?.length
      ? serviceSlugFromCatalog(sp.serviceId, allServices, sp.slug)
      : sp.slug;
    return !predicate || predicate(slug);
  });
}

const SURGE_HUB_ORDER: ServiceHubType[] = ['transport', 'delivery', 'on_demand'];

export interface SurgeServiceGroup {
  hub: ServiceHubType;
  label: string;
  services: HubServiceOption[];
}

/** Selected zone services grouped by hub for surge rule targeting. */
export function buildSurgeServiceGroups(
  form: ZoneFormState,
  allServices: HubServiceOption[],
): SurgeServiceGroup[] {
  const selected = new Set(form.selectedServiceIds);
  const byHub: Record<ServiceHubType, HubServiceOption[]> = {
    transport: [],
    delivery: [],
    on_demand: [],
  };

  for (const svc of allServices) {
    if (!selected.has(svc._id)) continue;
    if (svc.hubType === 'transport' || svc.hubType === 'delivery' || svc.hubType === 'on_demand') {
      byHub[svc.hubType].push(svc);
    }
  }

  return SURGE_HUB_ORDER
    .filter((hub) => byHub[hub].length > 0)
    .map((hub) => ({
      hub,
      label: HUB_META[hub].label,
      services: [...byHub[hub]].sort((a, b) => a.name.localeCompare(b.name)),
    }));
}

export function surgeServiceLabel(svc: HubServiceOption): string {
  return resolveServiceDisplayName(svc.hubType, svc.slug, svc.name) || svc.name;
}

export function formHasSelectedOnDemand(form: ZoneFormState, allServices: HubServiceOption[]) {
  const selected = new Set(form.selectedServiceIds);
  return allServices.some((s) => selected.has(s._id) && s.hubType === 'on_demand');
}

export function isOnDemandHubService(serviceId: string | undefined, allServices: HubServiceOption[]) {
  if (!serviceId) return false;
  return allServices.find((s) => s._id === serviceId)?.hubType === 'on_demand';
}

function parsePartnerPricingRaw(raw?: Record<string, unknown>): DeliveryPartnerPricingForm {
  if (!raw) return emptyDeliveryPartnerPricing();
  return {
    baseKm: Number(raw.baseKm ?? 3),
    baseAmount: Number(raw.baseAmount ?? 35),
    remainingKmRate: Number(raw.remainingKmRate ?? 6),
    pickupCharge: Number(raw.pickupCharge ?? 5),
    dropCharge: Number(raw.dropCharge ?? 5),
    waitingChargePerMinute: Number(raw.waitingChargePerMinute ?? 0),
    minimumGuarantee: Number(raw.minimumGuarantee ?? 50),
    maximumDeliveryKm: Number(raw.maximumDeliveryKm ?? 15),
    isActive: raw.isActive !== false,
  };
}

function parseDeliveryPartnerPricingFromMetadata(metadata?: Record<string, unknown>): DeliveryPartnerPricingForm {
  return parsePartnerPricingRaw(metadata?.deliveryPartnerPricing as Record<string, unknown> | undefined);
}

function parsePorterPartnerPricingFromMetadata(metadata?: Record<string, unknown>): DeliveryPartnerPricingForm {
  return parsePartnerPricingRaw(metadata?.porterPartnerPricing as Record<string, unknown> | undefined);
}

function parseIncentivesRaw(rows: unknown): DeliveryIncentiveForm[] {
  if (!Array.isArray(rows) || !rows.length) return emptyDeliveryIncentives();
  const parsed = rows.map((r, idx) => {
    const row = r as Record<string, unknown>;
    return newDeliveryIncentive({
      id: String(row.id || `inc_${idx}`),
      incentiveType: normalizeIncentiveType(String(row.incentiveType || 'peak_hour')),
      amount: Number(row.amount ?? 0),
      isActive: row.isActive !== false,
    });
  });
  return dedupeIncentivesByType(parsed);
}

function parseDeliveryIncentivesFromMetadata(metadata?: Record<string, unknown>): DeliveryIncentiveForm[] {
  return parseIncentivesRaw(metadata?.deliveryIncentives);
}

function parsePorterIncentivesFromMetadata(metadata?: Record<string, unknown>): DeliveryIncentiveForm[] {
  return parseIncentivesRaw(metadata?.porterIncentives);
}

function parsePorterMastersFromMetadata(metadata?: Record<string, unknown>): PorterZoneMastersForm {
  const raw = metadata?.porterMasters as Record<string, unknown> | undefined;
  if (!raw) return emptyPorterMasters();
  const asIds = (v: unknown) => (Array.isArray(v) ? v.map(String).filter(Boolean) : []);
  return {
    vehicleIds: asIds(raw.vehicleIds),
    categoryIds: asIds(raw.categoryIds),
    packageIds: asIds(raw.packageIds),
  };
}



export interface ZoneFormState {

  code: string;

  name: string;

  zoneLevel: 'PRIMARY' | 'SECONDARY';

  primaryZoneId: string;

  country: string;

  currency: string;

  timezone: string;

  unit: 'KM' | 'MILE';

  color: string;

  paymentTypes: string[];

  defaultPaymentType: string;

  biddingEnabled: boolean;

  allowDriverCounterOffer: boolean;

  autoAcceptLowestBid: boolean;

  nonServiceZone: boolean;

  polygon: { lat: number; lng: number }[];

  /** Land-only fill rings for map display. Service coverage uses `polygon` boundary. */
  landPolygons: { lat: number; lng: number }[][];

  selectedServiceIds: string[];

  selectedVehicleTypeCodes: string[];

  servicePricing: ServiceSchedulePricing[];

  vehiclePricing: VehicleSchedulePricing[];

  surgePricing: SurgeRow[];

  commissionRate: number;

  /** How drivers/partners earn in this zone — not tenant SaaS subscription */
  partnerPayoutModel: PartnerPayoutModel;

  /** Food & grocery delivery partner earnings (one config per zone) */
  deliveryPartnerPricing: DeliveryPartnerPricingForm;

  deliveryIncentives: DeliveryIncentiveForm[];

  /** Porter partner earnings — separate from food/grocery */
  porterPartnerPricing: DeliveryPartnerPricingForm;

  porterIncentives: DeliveryIncentiveForm[];

  /** Zone-scoped Porter masters selection */
  porterMasters: PorterZoneMastersForm;

}



const RIDE_HAILING_SLUGS = new Set(['transport']);

const VEHICLE_TO_SERVICE_SLUG: Record<string, string> = {
  bike: 'transport',
  auto: 'transport',
  sedan: 'transport',
  mini: 'transport',
  suv: 'transport',
};

export function isRideHailingService(slug: string) {
  return RIDE_HAILING_SLUGS.has(slug);
}

export function serviceSlugForVehicle(_vehicleCode: string) {
  return 'transport';
}



export function emptyZoneForm(): ZoneFormState {

  return {

    code: '',

    name: '',

    zoneLevel: 'PRIMARY',

    primaryZoneId: '',

    country: '',

    currency: '',

    timezone: '',

    unit: 'KM',

    color: '#2563eb',

    paymentTypes: ['cash', 'wallet', 'card'],

    defaultPaymentType: 'cash',

    biddingEnabled: false,

    allowDriverCounterOffer: true,

    autoAcceptLowestBid: false,

    nonServiceZone: false,

    polygon: [],

    landPolygons: [],

    selectedServiceIds: [],

    selectedVehicleTypeCodes: [],

    servicePricing: [],

    vehiclePricing: [],

    surgePricing: [],

    commissionRate: 15,

    partnerPayoutModel: 'commission',

    deliveryPartnerPricing: emptyDeliveryPartnerPricing(),

    deliveryIncentives: emptyDeliveryIncentives(),

    porterPartnerPricing: emptyDeliveryPartnerPricing(),

    porterIncentives: emptyDeliveryIncentives(),

    porterMasters: emptyPorterMasters(),

  };

}



export function buildServicePricingRow(svc: HubServiceOption): ServiceSchedulePricing {

  return {

    serviceId: svc._id,

    serviceName: svc.name,

    slug: svc.slug,

    hubType: svc.hubType,

    rideNow: { ...EMPTY_PRICING },

    rideLater: { ...EMPTY_PRICING },

  };

}



export function buildVehiclePricingRow(

  vehicle: VehicleTypeOption,

  allServices: HubServiceOption[]

): VehicleSchedulePricing | null {

  const slug = serviceSlugForVehicle(vehicle.code);

  const svc = allServices.find((s) => s.slug === slug);

  if (!svc) return null;

  return {

    vehicleTypeCode: vehicle.code,

    vehicleTypeName: vehicle.name,

    linkedServiceId: svc._id,

    rideNow: { ...EMPTY_PRICING },

    rideLater: { ...EMPTY_PRICING },

  };

}

export type PorterCatalogOptions = {
  vehicles: Array<{ _id: string }>;
  categories: Array<{ _id: string }>;
  packages: Array<{ _id: string }>;
};

function porterMastersEmpty(masters: PorterZoneMastersForm) {
  return !masters.vehicleIds.length && !masters.categoryIds.length && !masters.packageIds.length;
}

/** Fill vehicle/porter defaults only after the user has selected those services. */
export function applyDefaultZoneCatalog(
  form: ZoneFormState,
  catalog: HubServiceOption[],
  _vehicleTypes: VehicleTypeOption[],
  porterCatalog?: PorterCatalogOptions | null,
): ZoneFormState {
  if (!catalog.length) return form;

  let next = form;
  const clone = () => {
    if (next === form) next = { ...form, porterMasters: { ...form.porterMasters } };
  };

  const hasPorter = next.selectedServiceIds.some((id) => {
    const svc = catalog.find((s) => s._id === id);
    return Boolean(svc && isPorterService(svc.slug));
  });
  if (hasPorter && porterCatalog && porterMastersEmpty(next.porterMasters)) {
    clone();
    next.porterMasters = {
      vehicleIds: porterCatalog.vehicles.map((v) => v._id),
      categoryIds: porterCatalog.categories.map((c) => c._id),
      packageIds: porterCatalog.packages.map((p) => p._id),
    };
  }

  return next;
}



export function parseZonePartnerPayoutFromMetadata(metadata?: Record<string, unknown>): {
  partnerPayoutModel: PartnerPayoutModel;
  commissionRate: number;
} {
  const m = metadata || {};
  const commissionRate = Number(m.commissionRate ?? 15);
  if (m.partnerPayoutModel === 'commission' || m.partnerPayoutModel === 'subscription' || m.partnerPayoutModel === 'both') {
    return { partnerPayoutModel: m.partnerPayoutModel, commissionRate };
  }
  if (m.subscriptionRequired) {
    return { partnerPayoutModel: 'subscription', commissionRate };
  }
  return { partnerPayoutModel: 'commission', commissionRate };
}

export function formToApiPayload(form: ZoneFormState) {
  const vehicleRows = activeVehiclePricing(form)
    .filter((vp) => vp.linkedServiceId)
    .flatMap((vp) => [

    {

      serviceId: vp.linkedServiceId,

      vehicleTypeCode: vp.vehicleTypeCode,

      scheduleType: 'ride_now' as const,

      ...vp.rideNow,

    },

    {

      serviceId: vp.linkedServiceId,

      vehicleTypeCode: vp.vehicleTypeCode,

      scheduleType: 'ride_later' as const,

      ...vp.rideLater,

    },

  ]);



  const selected = new Set(form.selectedServiceIds);

  const serviceRows = form.servicePricing

    .filter((sp) => selected.has(sp.serviceId) && !isRideHailingService(sp.slug) && !usesZonePartnerPricing(sp.slug) && sp.serviceId)

    .flatMap((sp) => [

      { serviceId: sp.serviceId, scheduleType: 'ride_now' as const, ...sp.rideNow },

      { serviceId: sp.serviceId, scheduleType: 'ride_later' as const, ...sp.rideLater },

    ]);

  const deliveryCustomerRows = form.servicePricing

    .filter((sp) => selected.has(sp.serviceId) && usesZonePartnerPricing(sp.slug) && sp.serviceId)

    .map((sp) => ({

      serviceId: sp.serviceId,

      scheduleType: 'default' as const,

      ...sp.rideNow,

    }));



  const servicePricing = [...vehicleRows, ...serviceRows, ...deliveryCustomerRows];



  const surgePricing = form.surgePricing
    .filter((s) => !s.serviceId || selected.has(s.serviceId))
    .map((s) => {

    const [sh, sm] = s.startTime.split(':').map(Number);

    const [eh, em] = s.endTime.split(':').map(Number);

    return {

      serviceId: s.serviceId,

      name: s.name.trim(),

      multiplier: roundToDecimalPlaces(s.multiplier, ZONE_SURGE_MULTIPLIER_DECIMALS),

      surgePerKm: roundToDecimalPlaces(s.surgePerKm ?? 0, ZONE_PRICE_DECIMALS),

      startHour: sh || 0,

      endHour: eh || 23,

      startTime: s.startTime,

      endTime: s.endTime,

      daysOfWeek: s.daysOfWeek,

      serviceCategoryCode: s.serviceCategoryCode,

    };

  });



  return {

    code: form.code,

    name: form.name,

    zoneLevel: form.zoneLevel,

    primaryZoneId: form.zoneLevel === 'SECONDARY' ? form.primaryZoneId : undefined,

    country: form.country,

    currency: form.currency,

    timezone: form.timezone,

    unit: form.unit,

    color: form.color,

    paymentTypes: form.paymentTypes,

    defaultPaymentType: form.paymentTypes.includes(form.defaultPaymentType)
      ? form.defaultPaymentType
      : (form.paymentTypes[0] || 'cash'),

    biddingEnabled: form.biddingEnabled,

    nonServiceZone: form.nonServiceZone,

    polygon: form.polygon.map(({ lat, lng }) => ({ lat, lng })),

    serviceIds: form.selectedServiceIds,

    servicePricing,

    surgePricing,

    metadata: {

      commissionRate: form.commissionRate,

      partnerPayoutModel: form.partnerPayoutModel,

      ...(formHasSelectedFoodGrocery(form) ? {
        deliveryPartnerPricing: form.deliveryPartnerPricing,
        deliveryIncentives: dedupeIncentivesByType(form.deliveryIncentives),
      } : {}),

      ...(formHasSelectedPorter(form) ? {
        porterPartnerPricing: form.porterPartnerPricing,
        porterIncentives: dedupeIncentivesByType(form.porterIncentives),
        porterMasters: form.porterMasters,
      } : {}),

      coverLandOnly: false,

      ...(form.landPolygons.length ? { landPolygons: form.landPolygons } : {}),

      ...(form.selectedVehicleTypeCodes.length
        ? { selectedVehicleTypeCodes: form.selectedVehicleTypeCodes }
        : {}),

    },

  };

}


